'use client';

import clsx from 'clsx';
import VadImage from "vadimages-nextjs-image-optimizer";
import React, { useCallback, useEffect, useRef, useState } from "react";

import css from "./mouthpieces-mobile-slider.module.scss";
import type { IMouthpiecesMobileSliderProps } from './types';

// Converted from a Swiper carousel to a native CSS scroll-snap row so Swiper leaves the bundle.
// This slider was Swiper-coupled (centeredSlides + slideToLoop + activeIndex-driven onAction +
// the .swiper-slide-active underline), so the imperative Swiper API is re-implemented with scroll:
//  - slidesPerView:'auto' + centeredSlides -> snap-center cells (each 77px wide, set in the SCSS).
//  - clicking a slide -> scrollIntoView({inline:'center'}) on that cell (was swiper.slideToLoop).
//  - loop: DROPPED (finite scroll) -> comment. The centered-slide -> onAction() sync is driven by the
//    nearest-to-center cell on scroll (was Swiper onSlideChange + activeIndex).
//  - the active cell keeps the global `swiper-slide-active` class so the existing SCSS orange-underline
//    rule still applies untouched. Scrollbar -> hidden native scroll.
export function MouthpiecesMobileSlider({
    items,
    className,
    selectedItemTitle,
}: IMouthpiecesMobileSliderProps) {
    const trackRef = useRef<HTMLDivElement | null>(null);
    const cellRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    // Seed with 0 so the initially-centered item does NOT fire onAction on mount (Swiper's
    // onSlideChange likewise doesn't fire on init) — only real changes fire.
    const lastFiredRef = useRef<number>(0);

    const wrapperClasses = clsx(
        css["mouthpiece_slider"],
        className
    );

    // Find the cell whose center is nearest the track's horizontal center.
    const computeActive = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;
        const trackRect = track.getBoundingClientRect();
        const center = trackRect.left + trackRect.width / 2;
        let nearest = 0;
        let nearestDist = Infinity;
        cellRefs.current.forEach((cell, idx) => {
            if (!cell) return;
            const r = cell.getBoundingClientRect();
            const cellCenter = r.left + r.width / 2;
            const dist = Math.abs(cellCenter - center);
            if (dist < nearestDist) {
                nearestDist = dist;
                nearest = idx;
            }
        });
        setActiveIndex((prev) => (prev === nearest ? prev : nearest));
    }, []);

    // Fire onAction when the centered (active) item changes (was Swiper's onSlideChange).
    useEffect(() => {
        if (activeIndex === lastFiredRef.current) return;
        lastFiredRef.current = activeIndex;
        const item = items[activeIndex];
        if (item) item.onAction();
    }, [activeIndex, items]);

    // Debounce scroll handling to the next animation frame.
    const rafRef = useRef<number | null>(null);
    const handleScroll = useCallback(() => {
        if (rafRef.current !== null) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            computeActive();
        });
    }, [computeActive]);

    useEffect(() => {
        computeActive();
        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
    }, [computeActive]);

    const handleSlideTo = useCallback((slideIndex: number) => () => {
        const cell = cellRefs.current[slideIndex];
        cell?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }, []);

    return (
        <div className={wrapperClasses}>
            <section className={css["mouthpiece_slider__wrapper"]}>
                <div className="container">
                    <div
                        ref={trackRef}
                        onScroll={handleScroll}
                        // Side padding = 50% of the track minus half a 77px slide, so the first/last
                        // items can rest at the visual center (replicates Swiper centeredSlides).
                        className="flex items-stretch overflow-x-auto overflow-y-hidden snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden gap-2 px-[calc(50%-38px)]"
                        style={{ WebkitOverflowScrolling: "touch" }}
                    >
                        {items.map((item, index) => (
                            <div
                                key={index}
                                ref={(el) => { cellRefs.current[index] = el; }}
                                onClick={handleSlideTo(index)}
                                className={clsx("shrink-0 snap-center relative w-[77px]", activeIndex === index && "swiper-slide-active")}
                            >
                                <div className={css["content"]} style={{ height: '132px' }}>
                                    <button data-key={item.key} type="button" className='flex items-end'>
                                        <VadImage
                                            src={
                                                item.caption.includes('Snaptix')
                                                    ? `${item.imgPreviewSrc.replace('.png', '')}-closed.png`
                                                    : item.imgPreviewSrc
                                            }
                                            width={item.imgPreviewWidth}
                                            height={item.imgPreviewHeight}
                                            alt={item.caption}
                                            useOnlyOneDomain={true}
                                        />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <span>{selectedItemTitle.replace('<br>', ' ')}</span>
                </div>
            </section>
        </div>
    );
}
